
import * as React from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { CheckIcon } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const checkboxVariants = cva(
  'peer shrink-0 rounded-sm border border-input ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground flex-shrink-0',
  {
    variants: {
      size: {
        sm: 'h-3 w-3',
        md: 'h-4 w-4',
        lg: 'h-5 w-5',
        xl: 'h-6 w-6',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const checkboxIconVariants = cva(
  'flex items-center justify-center text-current transition-none',
  {
    variants: {
      size: {
        sm: 'text-[8px]',
        md: 'text-xs',
        lg: 'text-sm',
        xl: 'text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

export interface CheckboxProps
  extends React.ComponentProps<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {}

function Checkbox({ className, size, ...props }: CheckboxProps) {
  const checkboxSize = size || 'md'

  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      data-size={checkboxSize}
      className={cn(checkboxVariants({ size: checkboxSize }), className)}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className={cn(checkboxIconVariants({ size: checkboxSize }))}
      >
        <CheckIcon
          className={cn(
            checkboxSize === 'sm' && 'h-2 w-2',
            checkboxSize === 'md' && 'h-3 w-3',
            checkboxSize === 'lg' && 'h-4 w-4',
            checkboxSize === 'xl' && 'h-5 w-5'
          )}
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox, checkboxVariants }
